import React, { useEffect, useState } from "react";
import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import UploadComponent from "../Components/uploadComponent";
import ReactQuill, { Quill } from "react-quill";
import { createBlogs } from "../../../store/slices/blogsSlice";
import { toast } from "react-toastify";

const Font = Quill.import("attributors/class/font");
Font.whitelist = ["arial","comic-sans","courier-new","inter","georgia","helvetica","lucida"];
Quill.register(Font, true);

const Size = Quill.import("attributors/class/size");
Size.whitelist = [
  "12px","14px","16px","18px","20px","22px","24px","26px","28px","30px",
  "32px","34px","36px","38px","40px","42px","44px","46px","48px","50px",
  "52px","54px","56px","58px","60px","62px","64px","66px","68px","70px",
  "72px","74px","76px","78px","80px","82px","84px","86px","88px","90px",
  "92px","94px","96px","98px","100px",
];
Quill.register(Size, true);

const emptyForm = {
  title: "",
  subTitle: "",
  description: "",
  data: "",
  category: "",
  coverImage: "",
  createdBy: null,
  fb_link: "",
  twitter_link: "",
  instagram_link: "",
  linkedin_link: "",
};

const CreateBlogModal = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const [formFields, setFormFields] = useState({ ...emptyForm });
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, trigger, formState: { errors }, setValue, reset } = useForm({
    defaultValues: { title: "", subTitle: "", description: "", data: "", category: "", coverImage: "" },
  });

  const [formFieldsValidation] = useState({
    title: { ...register("title", { required: "Title is required" }) },
    subTitle: { ...register("subTitle", { required: "Sub title is required" }) },
    data: { ...register("data", { required: "Blog content is required" }) },
    category: { ...register("category", { required: "Category is required" }) },
  });

  useEffect(() => {
    setValue("coverImage", file);
    setFormFields((prev) => ({ ...prev, coverImage: file }));
  }, [file]);

  const handleInput = (event, key) => {
    const value = key === "data" ? event : event.target.value;
    setValue(key, value);
    setFormFields((prev) => ({ ...prev, [key]: value }));
  };

  const createBlogsData = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) return;
      setIsSubmitting(true);
      const formData = new FormData();
      Object.entries(formFields).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          if (key === "coverImage" && value instanceof File) {
            formData.append("coverImage", value);
          } else {
            formData.append(key, value);
          }
        }
      });
      const result = await dispatch(createBlogs(formData));
      if (result?.payload?.id) {
        toast.success("Blog created successfully!");
        toggle();
      } else {
        toast.error("Failed to create blog. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
      setFormFields({ ...emptyForm });
      setFile(null);
    }
  }, [isOpen]);

  const modules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }],
      [{ font: Font.whitelist }, { size: Size.whitelist }],
      [{ align: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      [{ color: [] }, { background: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
    clipboard: { matchVisual: false },
  };

  const formats = [
    "header","font","align","size","bold","italic","underline","strike",
    "color","background","blockquote","list","bullet","indent","link","image","video",
  ];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Create Blog</ModalHeader>
      <ModalBody>
        <div className="px-2">
          <div className="d-flex gap-2 mt-2">
            <div className="w-full">
              <Label>Title<span className="text-danger">*</span></Label>
              <Input name="title" type="text" placeholder="Enter Title" {...formFieldsValidation.title} onChange={(e) => handleInput(e, "title")} value={formFields.title} />
              {errors.title && <span className="mt-1 text-danger">{errors.title.message}</span>}
            </div>
            <div className="w-full">
              <Label>Sub Title<span className="text-danger">*</span></Label>
              <Input name="subTitle" type="text" placeholder="Enter Sub Title" {...formFieldsValidation.subTitle} onChange={(e) => handleInput(e, "subTitle")} value={formFields.subTitle} />
              {errors.subTitle && <span className="mt-1 text-danger">{errors.subTitle.message}</span>}
            </div>
          </div>
          <div className="d-flex gap-2 mt-4">
            <div className="w-full">
              <Label>Category Name<span className="text-danger">*</span></Label>
              <Input name="category" type="text" placeholder="Enter Category" {...formFieldsValidation.category} onChange={(e) => handleInput(e, "category")} value={formFields.category} />
              {errors.category && <span className="mt-1 text-danger">{errors.category.message}</span>}
            </div>
          </div>
          <div className="d-flex gap-2 mt-4">
            <div className="w-full">
              <Label>Description / Summary</Label>
              <Input name="description" type="textarea" rows={3} placeholder="Short description shown on the blog card listing page (optional)" onChange={(e) => handleInput(e, "description")} value={formFields.description} />
            </div>
          </div>
          <div className="mt-4">
            <Label className="fw-bold">Social Media Links <span className="text-muted fw-normal">(Optional)</span></Label>
            <div className="d-flex gap-2 mt-2">
              <div className="w-full">
                <Input name="fb_link" type="url" placeholder="Facebook URL" onChange={(e) => handleInput(e, "fb_link")} value={formFields.fb_link} />
              </div>
              <div className="w-full">
                <Input name="twitter_link" type="url" placeholder="Twitter (X) URL" onChange={(e) => handleInput(e, "twitter_link")} value={formFields.twitter_link} />
              </div>
            </div>
            <div className="d-flex gap-2 mt-2">
              <div className="w-full">
                <Input name="instagram_link" type="url" placeholder="Instagram URL" onChange={(e) => handleInput(e, "instagram_link")} value={formFields.instagram_link} />
              </div>
              <div className="w-full">
                <Input name="linkedin_link" type="url" placeholder="LinkedIn URL" onChange={(e) => handleInput(e, "linkedin_link")} value={formFields.linkedin_link} />
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 mt-4">
            <div className="w-full">
              <Label>Cover Image</Label>
              <UploadComponent addFile={setFile} />
            </div>
          </div>
          <div className="d-flex gap-2 mt-4">
            <div className="w-full">
              <Label>Blog Content<span className="text-danger">*</span></Label>
              <ReactQuill theme="snow" modules={modules} formats={formats} value={formFields.data} onChange={(val) => handleInput(val, "data")} />
              {errors.data && <span className="mt-1 text-danger">{errors.data.message}</span>}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="d-flex customModalFooter">
        <button className="btn btn-secondary" onClick={toggle} disabled={isSubmitting}>Cancel</button>
        <button className="btn bg-primary" onClick={createBlogsData} disabled={isSubmitting}>{isSubmitting ? "Creating..." : "Create"}</button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateBlogModal;
